import React, { useId, useState } from "react";
import { SubscriptionActionFooter } from "./SubscriptionActionDialog";
import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import Note from "../../library/Note";
import * as adminSubscriptionServices from "../../../contexts/services/admin/adminSubscriptionServices";
import { safeDate } from "../../utils";

const MEMBER_STATUSES = new Set(["active", "trialing", "past_due"]);

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export default function SubscriptionRenewCycleAction({
  subscriptionId,
  status,
  nextBillingDate,
  updating,
  runActionWithMiddleware,
  setUpdating,
  onComplete,
  onError,
}) {
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const allowed = MEMBER_STATUSES.has(status);
  const billingLabel = nextBillingDate ? safeDate(nextBillingDate) : "not set";

  const prepare = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      onError("Enter a reason for this change.");
      return;
    }

    runActionWithMiddleware({
      actionKey: "prepareSubscriptionRenewal",
      details: (
        <Typography variant='body2' color='text.secondary'>
          Prepare the renewal bill for {billingLabel}? This replaces any unpaid
          renewal bill for that date. The billing date and paid bills stay the
          same. The new bill still needs to be paid.
        </Typography>
      ),
      execute: async () => {
        setUpdating(true);
        try {
          const body = await adminSubscriptionServices.prepareRenewCycle(
            subscriptionId,
            { reason: trimmedReason }
          );
          const invoice = body?.data?.invoice;
          const amount =
            invoice?.charge_after_discount == null
              ? ""
              : ` Bill total after discount: SGD ${(invoice.charge_after_discount / 100).toFixed(2)}.`;
          setReason("");
          await onComplete(`${body?.message || "Renewal bill is ready."}${amount}`);
        } catch (error) {
          onError(
            errorMessage(error, "Could not prepare the renewal bill. Please try again.")
          );
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  if (!allowed) {
    return (
      <Note title='Renewal bill is not available right now'>
        <Typography variant='body2' color='text.secondary'>
          You can prepare this bill when the subscription is active, on a free
          trial, or has an overdue payment.
        </Typography>
      </Note>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Note title='Prepare the renewal bill'>
        <Stack spacing={1}>
          <Typography variant='body2' fontWeight={600}>
            Billing date: {billingLabel}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Uses your plan, current users, active companies and discount.
            Includes any scheduled plan change.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Replaces the unpaid renewal bill for this date. Paid bills and the
            billing date stay the same. The new bill still needs to be paid.
          </Typography>
        </Stack>
      </Note>
      <Note title='Avoid charging twice'>
        <Typography variant='body2' color='text.secondary'>
          If this billing period has started, unpaid charges already covered by
          the new bill are removed. Older charges, and charges for users or
          companies already removed, are kept.
        </Typography>
      </Note>
      {!nextBillingDate && (
        <Note title='Set a billing date first'>
          <Typography variant='body2' color='text.secondary'>
            A billing date is needed before you can prepare this bill.
          </Typography>
        </Note>
      )}
      <Stack spacing={1}>
        <Typography
          component='label'
          htmlFor={reasonId}
          variant='body2'
          fontWeight={600}
        >
          Reason for this change
        </Typography>
        <TextField
          id={reasonId}
          size='small'
          placeholder='For example: update the bill after a plan change'
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={updating}
          required
          fullWidth
        />
      </Stack>
      <SubscriptionActionFooter>
        <Button
          variant='contained'
          disableElevation
          onClick={prepare}
          disabled={updating || !reason.trim() || !nextBillingDate}
          startIcon={
            updating ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <AutorenewIcon fontSize='small' />
            )
          }
          sx={{ textTransform: "none" }}
        >
          {updating ? "Preparing..." : "Prepare renewal bill"}
        </Button>
      </SubscriptionActionFooter>
    </Stack>
  );
}
